import { ApiProperty } from '@nestjs/swagger';
import { Transform, Type } from 'class-transformer';
import {
  ArrayNotEmpty,
  IsBoolean,
  IsDateString,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  Min,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import type { ApiServerEntityIDType } from '../../../types';
import { LanguageCode } from '../../resource/language';
import {
  CAREER_IDS_WITH_AFTER_YH_SCHOOL_PARAMETER,
  CAREER_IDS_WITH_AUTO_SELECTION_PARAMETER,
  CAREER_IDS_WITH_CHILDREN_PARAMETER,
  CAREER_IDS_WITH_CHILD_WITH_DISABILITIES_PARAMETER,
  CAREER_IDS_WITH_ENDED_AT_PARAMETER,
  CAREER_IDS_WITH_REGULAR_SPECIALIST_PARAMETER,
  CAREER_IDS_WITH_SICK_CHILD_PARAMETER,
  CAREER_IDS_WITH_STARTED_AT_PARAMETER,
  CAREER_IDS_WITH_TRANSLATION_TITLE_PARAMETER,
} from '../../../const/catalog/item';
import { CHILD_MAX_AGE, CHILD_MIN_AGE } from '../../../const/common';
import { validatorMessageFromDictionary } from '../../../utils/validation/i18n/createMessagesDictionary';
import { IsGeoPoint } from '../../../utils/validation/decorator/isGeoPoint';

export class VacancyCreateRequestTranslation {
  careerId?: number;

  @ApiProperty({ enum: LanguageCode, enumName: 'LanguageCode' })
  @IsEnum(LanguageCode)
  languageCode: LanguageCode;

  @ApiProperty()
  @ValidateIf(
    (ctx: VacancyCreateRequestTranslation) =>
      typeof ctx.careerId !== 'undefined' && CAREER_IDS_WITH_TRANSLATION_TITLE_PARAMETER.includes(ctx.careerId),
  )
  @IsString({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо заполнить название',
    }),
  })
  @IsNotEmpty({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо заполнить название',
    }),
  })
  title?: string;

  @ApiProperty()
  @IsString({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо заполнить описание',
    }),
  })
  @IsNotEmpty({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо заполнить описание',
    }),
  })
  description: string;
}

export class VacancyCreateRequestChildren {
  @ApiProperty()
  @IsNumber()
  @Min(CHILD_MIN_AGE, {
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: `Минимальный возраст ребенка ${CHILD_MIN_AGE} лет`,
    }),
  })
  @Max(CHILD_MAX_AGE, {
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: `Максимальный возраст ребенка ${CHILD_MAX_AGE} лет`,
    }),
  })
  age: number;
}

export class VacancyCreateRequest {
  @ApiProperty()
  @IsNumber()
  @Min(1)
  careerId: ApiServerEntityIDType;

  @ApiProperty()
  @ValidateIf((o) => CAREER_IDS_WITH_STARTED_AT_PARAMETER.includes(o.careerId))
  @IsString({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо указать время начала',
    }),
  })
  @IsNotEmpty({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо указать время начала',
    }),
  })
  @IsDateString(undefined, {
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо указать время начала',
    }),
  })
  startedAt?: string | null;

  @ApiProperty()
  @ValidateIf((o) => CAREER_IDS_WITH_ENDED_AT_PARAMETER.includes(o.careerId))
  @IsString({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо указать время окончания',
    }),
  })
  @IsNotEmpty({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо указать время окончания',
    }),
  })
  @IsDateString(undefined, {
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо указать время окончания',
    }),
  })
  endedAt?: string | null;

  @ApiProperty()
  @IsString({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо заполнить адрес',
    }),
  })
  @IsNotEmpty({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо заполнить адрес',
    }),
  })
  address: string;

  @ApiProperty()
  @IsString()
  @IsNotEmpty()
  @IsGeoPoint()
  location: string;

  @ApiProperty({ type: VacancyCreateRequestTranslation, isArray: true })
  @Type(() => VacancyCreateRequestTranslation)
  @Transform(({ value, obj }) => {
    value.forEach((item: VacancyCreateRequestTranslation) => {
      item.careerId = obj.careerId;
    });
    return value;
  })
  @ValidateNested({ each: true })
  @ArrayNotEmpty()
  translations: VacancyCreateRequestTranslation[];

  @ApiProperty({ required: false })
  @Type(() => VacancyCreateRequestChildren)
  @ValidateIf((o) => CAREER_IDS_WITH_CHILDREN_PARAMETER.includes(o.careerId))
  @ValidateNested()
  @ArrayNotEmpty({
    message: validatorMessageFromDictionary({
      [LanguageCode.Russian]: 'Необходимо указать как минимум одного ребенка',
    }),
  })
  children?: VacancyCreateRequestChildren[];

  @ApiProperty({ required: false })
  @IsOptional()
  @ValidateIf((o) => CAREER_IDS_WITH_CHILD_WITH_DISABILITIES_PARAMETER.includes(o.careerId))
  @IsBoolean()
  childWithDisabilities?: boolean;

  @ApiProperty()
  @IsOptional()
  @ValidateIf((o) => CAREER_IDS_WITH_REGULAR_SPECIALIST_PARAMETER.includes(o.careerId))
  @IsBoolean()
  regularSpecialist: boolean;

  @ApiProperty()
  @IsOptional()
  @ValidateIf((o) => CAREER_IDS_WITH_AFTER_YH_SCHOOL_PARAMETER.includes(o.careerId))
  @IsBoolean()
  trainingCompleted?: boolean;

  @ApiProperty()
  @IsOptional()
  @ValidateIf((o) => CAREER_IDS_WITH_AUTO_SELECTION_PARAMETER.includes(o.careerId))
  @IsBoolean()
  autoSelection?: boolean;

  @ApiProperty()
  @IsOptional()
  @ValidateIf((o) => CAREER_IDS_WITH_SICK_CHILD_PARAMETER.includes(o.careerId))
  @IsBoolean()
  sickChild?: boolean;
}
